import { ChevronDown } from 'lucide-react'
import { createContext, useContext, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { type QuoteCardSection, QuotesEvent, useTrackQuotesEvent } from './quotesAnalytics'

interface DisclosureProps {
  /** Stable analytics key for the row, independent of the label's wording. */
  section: QuoteCardSection
  label: string
  /** One-line value shown right-aligned even while collapsed, e.g. "Admitted". */
  summary?: string
  /** Ignored inside a {@link DisclosureGroup}, which starts with every row closed. */
  defaultOpen?: boolean
  /** Omit for a carrier we have nothing sourced for — the row renders static, with no chevron. */
  children?: React.ReactNode
}

/** Label plus its one-line summary, at the same height as the expandable row below and a CarrierDetailRow. */
function StaticRow({ label, summary }: { label: string; summary?: string }) {
  return (
    <div className="flex w-full items-baseline justify-between gap-2 px-2 py-4 text-xs">
      <span className="text-foreground font-medium">{label}</span>
      {summary && <span className="text-ink-300 font-normal">{summary}</span>}
    </div>
  )
}

interface DisclosureGroupValue {
  openSection: QuoteCardSection | null
  setOpenSection: (section: QuoteCardSection | null) => void
}

const DisclosureGroupContext = createContext<DisclosureGroupValue | null>(null)

/** Rows inside open one at a time: opening one closes whichever was open. */
export function DisclosureGroup({ children }: { children: React.ReactNode }) {
  const [openSection, setOpenSection] = useState<QuoteCardSection | null>(null)
  const value = useMemo(() => ({ openSection, setOpenSection }), [openSection])
  return <DisclosureGroupContext value={value}>{children}</DisclosureGroupContext>
}

/**
 * Local expand/collapse toggle for the QuoteCard prototype. Scoped here (not
 * frontend/src/components/ui) since the hierarchy of what's collapsed vs. shown by
 * default is still being tested.
 */
export function Disclosure({ section, label, summary, defaultOpen = false, children }: DisclosureProps) {
  const group = useContext(DisclosureGroupContext)
  const [ownOpen, setOwnOpen] = useState(defaultOpen)
  const open = group ? group.openSection === section : ownOpen
  const track = useTrackQuotesEvent()

  if (!children) return <StaticRow label={label} summary={summary} />

  function toggle() {
    track(QuotesEvent.CARD_SECTION_TOGGLED, { section, opened: !open })
    if (group) group.setOpenSection(open ? null : section)
    else setOwnOpen(!open)
  }

  return (
    <div>
      <Button
        type="button"
        variant="ghost"
        onClick={toggle}
        aria-expanded={open}
        className="group hover:bg-ink-50 h-auto w-full justify-between gap-2 rounded-sm px-2 py-4 text-left font-normal has-[>svg]:px-2"
      >
        <span className="text-foreground text-xs font-medium">{label}</span>
        {summary && <span className="text-muted-foreground ml-auto text-xs font-normal">{summary}</span>}
        <ChevronDown
          className={cn(
            'text-ink-300 group-hover:text-foreground size-3.5 shrink-0 transition-transform duration-300 ease-out motion-reduce:transition-none',
            open && 'rotate-180'
          )}
        />
      </Button>
      {/* Stays mounted so height can animate; inert keeps a closed drawer out of tab order. */}
      <div
        inert={!open}
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          {/* px-2 matches the trigger's own padding, so the expanded content shares a left
              edge with the label above it rather than hanging into the card's gutter. */}
          <div className="px-2 pb-6">{children}</div>
        </div>
      </div>
    </div>
  )
}
